import { T, Var } from 'gt-react';
import type {
  ArTasksAgingBucket,
  ArTasksSummaryResponse,
  ArTasksTab,
} from '@erp/rpc';
import type { ReactNode } from 'react';
import { MetricCard } from '@/components/accounts-receivable/MetricCard';
import {
  AgingCard,
  type AgingBucketView,
} from '@/components/tasks/TaskAgingCard';
import { formatArTaskAmount } from './arTasksColumns';

/** Green within the 5-day window; red once late (as in the table), darker red when very late. */
const AR_TASKS_AGING_BUCKET_VIEWS: ReadonlyArray<
  AgingBucketView<ArTasksAgingBucket>
> = [
  { key: '0-5', barClass: 'bg-emerald-500', textClass: 'text-emerald-700' },
  { key: '6-14', barClass: 'bg-red-500', textClass: 'text-red-700' },
  { key: '15+', barClass: 'bg-red-800', textClass: 'text-red-900' },
];

const EMPTY_AR_TASKS_AGING: ArTasksSummaryResponse['aging'] = {
  '0-5': 0,
  '6-14': 0,
  '15+': 0,
  '0-2': 0,
  '3-7': 0,
  '8-14': 0,
};

/** Tasks past the 5-day window: every bucket after the first. */
function arTasksOverFiveDays(aging: ArTasksSummaryResponse['aging']): number {
  return aging['6-14'] + aging['15+'];
}

/** A metric subtitle that switches the table to that tab. */
function TabLink({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer underline-offset-2 hover:text-foreground hover:underline"
    >
      {children}
    </button>
  );
}

/** The four metric cards above the AR Tasks table. */
export function ArTasksSummaryCards({
  summary,
  isLoading,
  activeAging,
  onPickAging,
  onPickTab,
}: {
  summary: ArTasksSummaryResponse | undefined;
  isLoading: boolean;
  activeAging: ArTasksAgingBucket | undefined;
  onPickAging: (bucket: ArTasksAgingBucket) => void;
  onPickTab: (tab: ArTasksTab) => void;
}) {
  const aging = summary?.aging ?? EMPTY_AR_TASKS_AGING;
  const overFiveDays = arTasksOverFiveDays(aging);
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
      <MetricCard
        label={
          <T context="AR tasks metric card title">Shipped but not invoiced</T>
        }
        value={formatArTaskAmount(summary?.toInvoice.amount ?? '0')}
        subtitle={
          <TabLink onClick={() => onPickTab('to_invoice')}>
            <T context="AR tasks metric card subtitle, shipped orders count">
              <Var>{summary?.toInvoice.count ?? 0}</Var> shipped orders
            </T>
          </TabLink>
        }
        isLoading={isLoading}
      />
      <MetricCard
        label={
          <T context="AR tasks metric card title">Invoiced but not sent</T>
        }
        value={formatArTaskAmount(summary?.toSend.amount ?? '0')}
        subtitle={
          <TabLink onClick={() => onPickTab('to_send')}>
            <T context="AR tasks metric card subtitle, invoices count">
              <Var>{summary?.toSend.count ?? 0}</Var> invoices
            </T>
          </TabLink>
        }
        isLoading={isLoading}
      />
      <AgingCard
        aging={aging}
        buckets={AR_TASKS_AGING_BUCKET_VIEWS}
        active={activeAging}
        onPick={onPickAging}
        callout={
          overFiveDays > 0 && (
            <T context="AR tasks aging card, count of tasks pending more than 5 days">
              <Var>{overFiveDays}</Var> over 5 days
            </T>
          )
        }
      />
      <MetricCard
        label={<T context="AR tasks metric card title">Unassigned</T>}
        value={summary?.unassigned ?? 0}
        isLoading={isLoading}
      />
    </div>
  );
}
